import * as React from "react"
import type { UserInfoType } from "@/types"
import { useQuery } from "@tanstack/react-query"
import { Link, useNavigate } from "@tanstack/react-router"
import { clsx } from "clsx"
import Cookies from "js-cookie"

import { siteConfig } from "@/config/site"
import { fetchMenuTabs } from "@/lib/api/menu"
import { getUserPermissions, logout } from "@/lib/auth"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Label } from "@/components/ui/label"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInput,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar"
import { Icons } from "@/components/icons"
import { ModeToggle } from "@/components/mode-toggle"

import { useEmbed } from "../context/embed-context"

/**
 * Application sidebar component with navigation, search, and user profile controls
 */
export function AppSidebar() {
  const { isEmbed } = useEmbed()
  const clientInfoCookie = Cookies.get("user_client_info")
  const permissions = getUserPermissions()
  // State management
  const [searchTerm, setSearchTerm] = React.useState("")
  const [openSubMenu, setOpenSubMenu] = React.useState<number | null>(null)
  const { setOpen, collapsed, isMobile } = useSidebar()

  // Navigation and user data
  const navigate = useNavigate({ from: "/login" })
  const userInfoString = Cookies.get("user_info")
  let userInfo: UserInfoType | undefined

  if (userInfoString) {
    try {
      userInfo = JSON.parse(userInfoString) as UserInfoType
    } catch (error) {
      console.error("Failed to parse user_info cookie", error)
    }
  }

  // Data fetching
  const { data, isLoading } = useQuery({
    queryKey: ["menuTabs", clientInfoCookie], // ✅ include the cookie in key
    queryFn: fetchMenuTabs,
    enabled: !!clientInfoCookie, // ✅not trigger if no cookies
  })

  // System Config sub-item icons
  const systemConfigSubItemIcons = {
    Zones: <Icons.zones className="mr-2 size-4" />,
    "Meter Size": <Icons.meterSize className="mr-2 size-4" />,
    "Meter Reader Zone": <Icons.meterReader className="mr-2 size-4" />,
    "Books Zone": <Icons.bookZone className="mr-2 size-4" />,
    "Zone Connection Rates": <Icons.connectionRate className="mr-2 size-4" />,
    "Zone Policy Due Date": <Icons.policyDate className="mr-2 size-4" />,
    "Connection Status": <Icons.connectionStatus className="mr-2 size-4" />,
    "Connection Type": <Icons.connectionType className="mr-2 size-4" />,
    "SMS Templated Messages": <Icons.smsTemplate className="mr-2 size-4" />,
    "Email Templated Messages": <Icons.emailTemplate className="mr-2 size-4" />,
    "Billing User Info": <Icons.billingUser className="mr-2 size-4" />,
    Position: <Icons.position className="mr-2 size-4" />,
    Department: <Icons.department className="mr-2 size-4" />,
    "Meter Status": <Icons.circleGauge className="mr-2 size-4" />,
    "Meter Size Rates": <Icons.Proportions className="mr-2 size-4" />,
    "Commodity Rates": <Icons.HandCoins className="mr-2 size-4" />,
  } as const

  // Handlers
  const handleToggleSubMenu = (id: number) => {
    setOpenSubMenu((prev) => (prev === id ? null : id))
    // Don't call setOpen here
  }

  const toLogin = () => {
    logout()
    navigate({ to: "/login" })
  }

  // Filter menu tabs based on search and permissions
  const filteredMenuTabs = React.useMemo(() => {
    if (!data) return []

    return data
      .filter((item) => item.name && item.name?.toLowerCase() !== "settings")
      .filter((item) => {
        // Safety: ensure item.name exists (already filtered)
        const matchesMainMenu = item.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase())

        const matchingSubMenus = item.sub_menu?.filter((sub) =>
          // ✅ Guard against null sub.name
          sub.name?.toLowerCase().includes(searchTerm.toLowerCase()),
        )

        return (
          matchesMainMenu || (matchingSubMenus && matchingSubMenus.length > 0)
        )
      })
  }, [data, searchTerm])

  // Loading state
  if (isLoading) {
    return (
      <Sidebar collapsible="icon">
        <SidebarHeader>
          <div className="p-4 text-center">Loading menu...</div>
        </SidebarHeader>
      </Sidebar>
    )
  }

  return (
    <>
      <div className="fixed right-0 top-0 z-[500] p-2 lg:hidden">
        <SidebarTrigger />
      </div>
      <Sidebar collapsible="icon">
        <SidebarHeader>
          <SidebarMenu>
            {/* Logo and Mode Toggle */}
            <SidebarMenuItem className="flex items-center gap-0">
              {/* Logo Link */}
              <SidebarMenuButton size="lg" asChild>
                <div
                  className={clsx(
                    "flex items-center gap-0",
                    collapsed && "lg:hidden", // Hide only when collapsed on desktop
                  )}
                >
                  <Link to="/" className="-ml-4 flex flex-1 items-center">
                    <Icons.himo className="-mr-4 size-16" />
                    <div className="flex flex-col leading-none">
                      <span className="font-semibold">{siteConfig.name}</span>
                    </div>
                  </Link>
                </div>
              </SidebarMenuButton>

              {collapsed ? (
                <span>
                  <div className="center hidden lg:block">
                    <SidebarTrigger />
                  </div>
                  <div className="hidden lg:block">
                    <Icons.himo className="center h-[30px] w-[40px]" />
                  </div>
                  {/* Mobile-only ModeToggle (hidden on desktop when collapsed) */}
                  <div className="lg:hidden">
                    <ModeToggle />
                  </div>
                </span>
              ) : (
                <>
                  <ModeToggle />
                  <div className="hidden lg:block">
                    <SidebarTrigger />
                  </div>
                </>
              )}
            </SidebarMenuItem>
          </SidebarMenu>
          <SidebarSeparator />
          {/* Search Input */}
          <form className={collapsed ? "hidden" : ""}>
            <SidebarGroup className="flex py-2">
              <SidebarGroupContent className="relative">
                <Label htmlFor="search" className="sr-only">
                  Search
                </Label>
                <SidebarInput
                  id="search"
                  placeholder="Search"
                  className="pl-8"
                  autoComplete="off"
                  disabled={!data}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Icons.search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 select-none opacity-50" />
              </SidebarGroupContent>
            </SidebarGroup>
          </form>
        </SidebarHeader>

        {/* Main Navigation Content */}
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {filteredMenuTabs.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground">
                    No menu items
                  </div>
                ) : (
                  filteredMenuTabs.map((link) => {
                    const Icon = Icons[link.icon]
                      ? Icons[link.icon]
                      : () => null
                    const hasSubMenuItems =
                      link.sub_menu && link.sub_menu.length > 0
                    const CollapseIcon = hasSubMenuItems ? Icons.right : null

                    if (!isMobile && collapsed && hasSubMenuItems) {
                      return null
                    }

                    return (
                      <React.Fragment key={link.id}>
                        {/* Main Menu Item */}
                        <SidebarMenuItem>
                          <SidebarMenuButton asChild tooltip={link.name}>
                            {hasSubMenuItems ? (
                              <button
                                onClick={() => handleToggleSubMenu(link.id)}
                                className={`flex items-center rounded-lg px-3 py-2 transition-all [&.active]:bg-muted [&.active]:font-semibold [&.active]:text-primary`}
                              >
                                <Icon />
                                {(!collapsed || isMobile) && (
                                  <>
                                    {link.name}
                                    {CollapseIcon && (
                                      <CollapseIcon
                                        className={`ml-auto transition-transform duration-200 ${
                                          openSubMenu === link.id
                                            ? "rotate-90"
                                            : ""
                                        }`}
                                      />
                                    )}
                                  </>
                                )}
                              </button>
                            ) : permissions?.includes(link.permission) ? (
                              <Link
                                to={link.menu_route}
                                className={`flex items-center rounded-lg px-3 py-2 transition-all [&.active]:bg-muted [&.active]:font-semibold [&.active]:text-primary`}
                              >
                                <Icon />
                                {(!collapsed || isMobile) && link.name}
                              </Link>
                            ) : null}
                          </SidebarMenuButton>
                        </SidebarMenuItem>

                        {/* Submenu Items - show in mobile or when not collapsed */}
                        {(!collapsed || isMobile) &&
                          link.sub_menu &&
                          openSubMenu === link.id && (
                            <ScrollArea
                              className={`${link.sub_menu.length > 2 ? "h-[150px]" : null}`}
                            >
                              <SidebarMenuSub>
                                {link.sub_menu.map((subLink) => {
                                  const SubIcon =
                                    systemConfigSubItemIcons[
                                      subLink.name as keyof typeof systemConfigSubItemIcons
                                    ] || null
                                  return (
                                    <SidebarMenuSubItem key={subLink.id}>
                                      <SidebarMenuSubButton asChild>
                                        {permissions?.includes(
                                          subLink.permission,
                                        ) ? (
                                          <Link
                                            to={`${link.menu_route}${subLink.sub_menu_route}`}
                                            className={`${
                                              subLink.name?.length >= 21
                                                ? "py-5"
                                                : "py-3"
                                            } flex items-center rounded-lg py-2 text-muted-foreground transition-all [&.active]:bg-muted [&.active]:font-semibold [&.active]:text-primary`}
                                          >
                                            {SubIcon}
                                            {subLink.name}
                                          </Link>
                                        ) : null}
                                      </SidebarMenuSubButton>
                                    </SidebarMenuSubItem>
                                  )
                                })}
                              </SidebarMenuSub>
                            </ScrollArea>
                          )}
                      </React.Fragment>
                    )
                  })
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        {/* Footer with User Profile */}
        {!isEmbed && (
          <SidebarFooter>
            <SidebarMenu>
              {collapsed ? (
                <span className="flex w-full justify-center py-2">
                  <ModeToggle />
                </span>
              ) : null}
              <SidebarMenuItem>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton
                      size="lg"
                      className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                    >
                      <Avatar className="h-8 w-8 rounded-lg">
                        <AvatarFallback className="rounded-lg">
                          <Icons.user />
                        </AvatarFallback>
                      </Avatar>
                      <div className="grid flex-1 text-left text-sm leading-tight">
                        <span className="truncate font-semibold">
                          {userInfo?.full_name}
                        </span>
                        <span className="truncate text-xs">
                          {userInfo?.email}
                        </span>
                      </div>
                      <Icons.updown className="ml-auto size-4" />
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                    side="bottom"
                    align="end"
                    sideOffset={4}
                  >
                    <DropdownMenuLabel className="p-0 font-normal">
                      <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                        <Avatar className="h-8 w-8 rounded-lg">
                          <AvatarFallback className="rounded-lg">
                            <Icons.user />
                          </AvatarFallback>
                        </Avatar>
                        <div className="grid flex-1 text-left text-sm leading-tight">
                          <span className="truncate font-semibold">
                            {userInfo?.full_name}
                          </span>
                          <span className="truncate text-xs">
                            {userInfo?.email}
                          </span>
                        </div>
                      </div>
                    </DropdownMenuLabel>

                    <DropdownMenuSeparator />

                    <DropdownMenuGroup>
                      <DropdownMenuItem asChild>
                        <Link to="/settings/profile">
                          <Icons.user className="mr-1 size-4" />
                          Profile
                        </Link>
                      </DropdownMenuItem>

                      <DropdownMenuItem asChild>
                        <Link to="/settings/change-password">
                          <Icons.key className="mr-1 size-4" />
                          Change Password
                        </Link>
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />

                      <DropdownMenuItem onClick={toLogin}>
                        <Icons.logout className="mr-1 size-4" />
                        Log out
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
        )}
        <SidebarRail />
      </Sidebar>
    </>
  )
}
